import { workDependencyMapColors } from "@miplatform/constants";
import { IWorkOrderDependencyMaps, WorkEntityType } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";
import {
  IExtendedWorkOrderDependencyMaps,
  Label,
  SortedDependencyMap,
} from "./autoTrigger.types";

export const getColorByMainLabel = (index: number) =>
  workDependencyMapColors[index % workDependencyMapColors.length];

// ── Transform server data ───────────────────────────────────────────

const transformMap = (
  map: IWorkOrderDependencyMaps
): IExtendedWorkOrderDependencyMaps => {
  const offset = map.offsetToPrincipal ?? 0;
  return {
    ...map,
    hours: offset % 24,
    days: offset > 0 ? Math.floor(offset / 24) : Math.ceil(offset / 24),
    dependentOn: [],
  };
};

export const transformDependencyMaps = (
  data: IWorkOrderDependencyMaps[]
): SortedDependencyMap => {
  const result: SortedDependencyMap = {};

  data.forEach((map) => {
    const extended = transformMap(map);
    if (extended.dependsOn) {
      extended.dependentOn = data
        .filter((x) =>
          extended.dependsOn.find((y) => y.principalId === x.dependentId)
        )
        .map((x) => x.id);
    }
    (result[extended.days] ||= []).push(extended);
  });

  // Default an empty list for order day 0 — needed to display the order template card
  if (!result[0]) {
    result[0] = [];
  }

  return result;
};

// ── Dependency day validation ───────────────────────────────────────

export interface DependencyValidationResult {
  isValid: boolean;
  pastDependencies: {
    itemId: string;
    dependencyId: string;
    itemDay: number;
    dependencyDay: number;
  }[];
  cycles: string[][];
}

/**
 * Validates every dependency in a `SortedDependencyMap`:
 *  - No dependency points to an item scheduled in the past.
 *  - No circular dependency chains exist.
 *
 * Returns a `DependencyValidationResult` with `isValid` and details about
 * any past-dependencies or cycles that were found.
 */
export const validateDependencyMap = (
  map: SortedDependencyMap
): DependencyValidationResult => {
  const allItems = Object.values(map).flat();
  const itemById = new Map(allItems.map((item) => [item.id, item]));

  // ── 1. Detect dependencies in the past ────────────────────────────
  const pastDependencies: DependencyValidationResult["pastDependencies"] = [];

  allItems.forEach((item) => {
    item.dependentOn.forEach((depId) => {
      const dep = itemById.get(depId);
      if (dep && dep.days > item.days) {
        pastDependencies.push({
          itemId: item.id,
          dependencyId: dep.id,
          itemDay: item.days,
          dependencyDay: dep.days,
        });
      }
    });
  });

  // ── 2. Detect cycles (DFS) ────────────────────────────────────────
  const cycles: string[][] = [];
  const visited = new Set<string>();
  const inStack = new Set<string>();

  const dfs = (id: string, path: string[]) => {
    if (inStack.has(id)) {
      // Extract the cycle portion of the path
      const cycleStart = path.indexOf(id);
      cycles.push(path.slice(cycleStart));
      return;
    }
    if (visited.has(id)) return;

    visited.add(id);
    inStack.add(id);
    path.push(id);

    const item = itemById.get(id);
    if (item) {
      item.dependentOn.forEach((depId) => dfs(depId, [...path]));
    }

    inStack.delete(id);
  };

  allItems.forEach((item) => {
    if (!visited.has(item.id)) {
      dfs(item.id, []);
    }
  });

  return {
    isValid: pastDependencies.length === 0 && cycles.length === 0,
    pastDependencies,
    cycles,
  };
};

/**
 * Returns a map of itemId → error messages for items that have validation problems.
 */
export const getValidationErrorsByItemId = (
  validation: DependencyValidationResult
): Record<string, string[]> => {
  const errors: Record<string, string[]> = {};

  const addError = (id: string, message: string) => {
    (errors[id] ||= []).push(message);
  };

  validation.pastDependencies.forEach(({ itemId, dependencyDay }) => {
    addError(
      itemId,
      `Invalid dependency order. An offset cannot occur before the step it follows.`
    );
  });

  validation.cycles.forEach((cycle) => {
    cycle.forEach((id) => {
      addError(id, `Invalid dependency part of a circular dependency.`);
    });
  });

  return errors;
};

// ── Label derivation (replaces all manual label state) ──────────────

/**
 * Derive display labels purely from the dependency map structure.
 */
export const deriveLabels = (map: SortedDependencyMap): Label[] => {
  const labels: Label[] = [];
  const allItems = Object.values(map).flat();

  allItems.forEach((item) => {
    if (!item.dependencyLabel) return;

    const mainLabel = item.dependencyLabel;
    const colors = getColorByMainLabel(Number(mainLabel) - 1);

    // Self label — the number badge on the principal (e.g. "1.")
    labels.push({
      mapId: item.id,
      fromMapId: item.id,
      displayOnMapId: item.id,
      dependencyLabel: mainLabel,
      color: colors.backgroundColor,
      labelColor: colors.color,
    });

    // Cross-reference labels for each predecessor in dependentOn
    item.dependentOn.forEach((depId, index) => {
      const dep = allItems.find((x) => x.id === depId);
      if (!dep) return;

      const letter = String.fromCharCode("a".charCodeAt(0) + index);

      // Displayed on the principal: "1a. Dep Name"
      labels.push({
        mapId: dep.id,
        fromMapId: item.id,
        displayOnMapId: item.id,
        dependencyLabel: `${mainLabel}${letter}. ${dep.dependent.name}`,
        color: colors.backgroundColor,
        labelColor: colors.color,
      });

      // Displayed on the dependent: "1a"
      labels.push({
        mapId: dep.id,
        fromMapId: item.id,
        displayOnMapId: dep.id,
        dependencyLabel: `${mainLabel}${letter}`,
        color: colors.backgroundColor,
        labelColor: colors.color,
      });
    });
  });

  return labels;
};

// ── Label filters ───────────────────────────────────────────────────

/** Labels where the item itself is the source (self labels like "1.") */
export const getItemSelfLabels = (labels: Label[], itemId: string): Label[] =>
  labels.filter((l) => l.displayOnMapId === itemId && l.mapId === itemId);

/** Labels showing which items depend on this one (displayed on the item, referencing other items) */
export const getItemDependencyLabels = (
  labels: Label[],
  itemId: string
): Label[] =>
  labels.filter(
    (l) =>
      l.mapId !== itemId &&
      l.fromMapId === itemId &&
      l.displayOnMapId === itemId
  );

// ── Map mutations (all pure — return new maps) ─────────────────────

const getMaxDependencyLabel = (
  items: IExtendedWorkOrderDependencyMaps[]
): number | null => {
  let max: number | null = null;

  items.forEach((item) => {
    if (item.dependencyLabel != null) {
      const value = Number(item.dependencyLabel);
      if (!Number.isNaN(value)) {
        max = max === null ? value : Math.max(max, value);
      }
    }
  });

  return max;
};

/**
 * Rebalance all dependencyLabel numbers so they are sequential (1, 2, 3, …)
 * based on the current ordering of items across all day groups.
 * Letters are already derived from the dependentOn array index in deriveLabels,
 * so they stay sequential automatically.
 */
const rebalanceLabels = (map: SortedDependencyMap): SortedDependencyMap => {
  const days = Object.keys(map)
    .map(Number)
    .sort((a, b) => a - b);

  // Collect all items that have a numeric dependencyLabel, preserving their order
  const labelledItems: { day: number; index: number; currentLabel: number }[] =
    [];

  days.forEach((day) => {
    map[day].forEach((item, index) => {
      if (item.dependencyLabel != null) {
        const value = Number(item.dependencyLabel);
        if (!Number.isNaN(value)) {
          labelledItems.push({ day, index, currentLabel: value });
        }
      }
    });
  });

  // Sort by current label so the relative ordering is preserved
  labelledItems.sort((a, b) => a.currentLabel - b.currentLabel);

  // Check if already sequential — skip clone if nothing changed
  const alreadyBalanced = labelledItems.every(
    (item, i) => item.currentLabel === i + 1
  );
  if (alreadyBalanced) return map;

  // Clone and reassign sequential labels
  const newMap = { ...map };
  const clonedGroups = new Set<number>();

  labelledItems.forEach((entry, i) => {
    if (!clonedGroups.has(entry.day)) {
      newMap[entry.day] = [...newMap[entry.day]];
      clonedGroups.add(entry.day);
    }
    newMap[entry.day][entry.index] = {
      ...newMap[entry.day][entry.index],
      dependencyLabel: String(i + 1),
    };
  });

  return newMap;
};

export const addDependencyToMap = (
  map: SortedDependencyMap,
  principalId: string,
  principalDay: number,
  dependentId: string,
  shouldAdd: boolean
): SortedDependencyMap => {
  const newMap = { ...map };
  const dayGroup = [...(newMap[principalDay] ?? [])];
  const idx = dayGroup.findIndex((i) => i.id === principalId);
  if (idx === -1) return map;

  const principal = { ...dayGroup[idx] };

  if (shouldAdd) {
    principal.dependentOn = [...principal.dependentOn, dependentId];
    if (!principal.dependencyLabel) {
      const allItems = Object.values(map).flat();
      const maxLabel = getMaxDependencyLabel(allItems) ?? 0;
      principal.dependencyLabel = String(maxLabel + 1);
    }
  } else {
    principal.dependentOn = principal.dependentOn.filter(
      (id) => id !== dependentId
    );
    if (principal.dependentOn.length === 0) {
      principal.dependencyLabel = null;
    }
  }

  dayGroup[idx] = principal;
  dayGroup.sort(
    (a, b) => Number(a.dependencyLabel ?? 0) - Number(b.dependencyLabel ?? 0)
  );
  newMap[principalDay] = dayGroup;

  return rebalanceLabels(newMap);
};

export const moveItemInMap = (
  map: SortedDependencyMap,
  currentDay: number,
  itemId: string,
  targetDay: number
): SortedDependencyMap => {
  const newMap = { ...map };
  const currentGroup = [...(newMap[currentDay] ?? [])];
  const itemIndex = currentGroup.findIndex((item) => item.id === itemId);
  if (itemIndex === -1) return map;

  const movedItem = { ...currentGroup[itemIndex], days: targetDay };
  currentGroup.splice(itemIndex, 1);

  // Keep the day-0 group even when empty — needed for the order template card
  if (currentGroup.length > 0 || currentDay === 0) {
    newMap[currentDay] = currentGroup;
  } else {
    delete newMap[currentDay];
  }

  const targetGroup = [...(newMap[targetDay] ?? [])];
  targetGroup.push(movedItem);
  newMap[targetDay] = targetGroup;

  return newMap;
};

export const updateFieldInMap = <
  K extends keyof IExtendedWorkOrderDependencyMaps
>(
  map: SortedDependencyMap,
  day: number,
  itemId: string,
  field: K,
  value: IExtendedWorkOrderDependencyMaps[K]
): SortedDependencyMap => {
  const newMap = { ...map };
  const group = [...(newMap[day] ?? [])];
  const index = group.findIndex((item) => item.id === itemId);
  if (index === -1) return map;

  group[index] = { ...group[index], [field]: value };
  newMap[day] = group;

  return newMap;
};

// ── Upsert request builder ──────────────────────────────────────────

interface ICreateWorkTemplateDependencyMap {
  id?: string;
  principalId: string;
  principalType: WorkEntityType;
  dependentId: string;
  dependentType: WorkEntityType;
  offsetToPrincipal?: number;
  dependencyLabel?: string | null;
  offsetTargetProperty?: string | null;
}

const findOrCreateRequestItem = (
  serverMaps: IWorkOrderDependencyMaps[],
  principalId: string,
  principal: IExtendedWorkOrderDependencyMaps | null,
  dependentId: string
): ICreateWorkTemplateDependencyMap => {
  // Try to find an existing dependency map
  const existing = serverMaps.find(
    (x) => x.dependentId === dependentId && x.principalId === principalId
  );

  if (existing) {
    return {
      id: existing.id,
      principalId: existing.principalId,
      principalType: existing.principalType,
      dependentId: existing.dependentId,
      dependentType: existing.dependentType,
      offsetToPrincipal: principal
        ? principal.hours + principal.days * 24
        : undefined,
      dependencyLabel: principal ? principal.dependencyLabel : undefined,
      offsetTargetProperty: principal
        ? principal.offsetTargetProperty ?? "ApprovedStartDate"
        : undefined,
    };
  }

  // Try to find a sub dependsOn map (WorkOrder → WorkOrder)
  if (!principal) {
    const subMaps = serverMaps.flatMap((x) => x.dependsOn);
    const subExisting = subMaps.find(
      (x) => x.principalId === principalId && x.dependentId === dependentId
    );
    if (subExisting) {
      return {
        id: subExisting.id,
        principalId,
        principalType: WorkEntityType.WorkOrder,
        dependentId,
        dependentType: WorkEntityType.WorkOrder,
      };
    }
  }

  return {
    id: createUUID(),
    principalId,
    principalType: WorkEntityType.WorkOrder,
    dependentId,
    dependentType: WorkEntityType.WorkOrder,
  };
};

export const buildUpsertRequest = (
  sortedDependencyMap: SortedDependencyMap,
  serverMaps: IWorkOrderDependencyMaps[]
): ICreateWorkTemplateDependencyMap[] => {
  const allItems = Object.values(sortedDependencyMap).flat();
  const result: ICreateWorkTemplateDependencyMap[] = [];

  allItems.forEach((item) => {
    result.push(
      findOrCreateRequestItem(
        serverMaps,
        item.principalId,
        item,
        item.dependentId
      )
    );

    item.dependentOn.forEach((depId) => {
      const dep = allItems.find((x) => x.id === depId);
      if (dep) {
        result.push(
          findOrCreateRequestItem(
            serverMaps,
            dep.dependentId,
            null,
            item.dependentId
          )
        );
      }
    });
  });

  return result;
};
